import React, { useMemo, useState } from 'react';
import { AlertTriangle, Ban, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../Button';
import { ServiceItem, ServiceTransaction } from '../../types';
import { WAIVER_BLOCK_TEXT, waivableEntries, waivableQuantity } from '../../services/serviceWaiverLogic';
import { waivedQuantity } from '../../services/projectLv';

interface Props {
    service: ServiceItem;
    /** Das Leistungsprotokoll des Projekts — Grundlage der Auswahl unten. */
    serviceLog: ServiceTransaction[];
    /** Wirft nie — der Text der Rueckmeldung landet im Dialog. */
    onWaive: (quantity: number, reason: string, entryIds?: string[]) => Promise<{ ok: boolean; message: string }>;
    onRelease: (reason: string) => Promise<{ ok: boolean; message: string }>;
    onClose: () => void;
}

/**
 * VERZICHT auf eine erbrachte, aber nie abgerechnete Menge.
 *
 * Der Anlass: ein schlussgerechnetes Projekt, an dem eine Begehung als offen
 * stehen bleibt. Sie taucht sonst in jeder Abrechnungsliste wieder auf, und der
 * einzige Ausweg war bisher ein SQL-Eingriff von Hand.
 *
 * Der Dialog trennt die beiden Faelle ausdruecklich und im Klartext, weil das
 * Vermischen genau der teure Fehler ist: der Verzicht laesst den Leistungsstand
 * stehen (die Begehung HAT stattgefunden), die Ruecknahme einer Fehlbuchung
 * muesste ihn senken. Wer hier eine ausgefallene Begehung "wegverzichtet",
 * traegt sie fuer immer als erbracht im Leistungsnachweis.
 */
export const ServiceWaiverDialog: React.FC<Props> = ({ service, serviceLog, onWaive, onRelease, onClose }) => {
    const offen = waivableQuantity(service);
    const bereitsVerzichtet = waivedQuantity(service);

    // WELCHE Leistung gemeint ist. Die blanke Menge sagt bei acht Begehungen
    // nur, dass es irgendeine war — im Streitfall wertlos, und ein Jahr spaeter
    // weiss niemand mehr, welcher Termin nicht gefordert wurde.
    const eintraege = useMemo(() => waivableEntries(service, serviceLog), [service, serviceLog]);
    const waehlbar = useMemo(() => eintraege.filter((e) => !e.blocked), [eintraege]);

    const [modus, setModus] = useState<'entries' | 'quantity'>(waehlbar.length > 0 ? 'entries' : 'quantity');
    const [auswahl, setAuswahl] = useState<string[]>([]);
    const [menge, setMenge] = useState<string>(offen > 0 ? String(offen) : '');
    const [grund, setGrund] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const einheit = service.unit || '';
    const istAuswahl = modus === 'entries';
    const auswahlSumme = useMemo(
        () =>
            Math.round(
                eintraege
                    .filter((e) => auswahl.includes(String(e.entry.id)))
                    .reduce((sum, e) => sum + e.quantity, 0) * 1000,
            ) / 1000,
        [eintraege, auswahl],
    );
    // Eine Wahrheit fuer die Menge: im Auswahlmodus ist es die Summe der
    // angehakten Eintraege, sonst die Eingabe. Die Regel prueft beides erneut
    // gegeneinander (planWaiver), hier wird nichts nebenher gerechnet.
    const mengeZahl = istAuswahl ? auswahlSumme : Number((menge || '').replace(',', '.'));
    const mengeOk = Number.isFinite(mengeZahl) && mengeZahl > 0 && mengeZahl <= offen;
    const grundOk = grund.trim().length >= 3;

    const umschalten = (id: string) =>
        setAuswahl((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const tag = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('de-DE') : 'ohne Datum');

    const ausfuehren = async (aktion: () => Promise<{ ok: boolean; message: string }>) => {
        setLaeuft(true);
        setFehler(null);
        const ergebnis = await aktion();
        if (ergebnis.ok) {
            onClose();
            return;
        }
        setFehler(ergebnis.message);
        setLaeuft(false);
    };

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-amber-50 p-3 rounded-full"><Ban className="w-6 h-6 text-amber-600" /></div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg text-slate-800">Nicht mehr abrechnen</h3>
                        <p className="text-sm text-slate-600 mt-1 truncate" title={service.title}>
                            {service.lvPosition ? `${service.lvPosition} — ` : ''}{service.title}
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900 leading-relaxed">
                        <strong>Nur für erbrachte Leistung.</strong> Die Menge bleibt als erbracht
                        stehen und steht weiter im Leistungsnachweis — sie wird lediglich nicht mehr
                        gefordert. War die Begehung <em>gar nicht</em> (ausgefallen, doppelt gebucht),
                        gehört die <strong>Buchung zurückgenommen</strong> statt verzichtet, sonst
                        zählt sie für immer als erbracht.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Erbracht</div>
                        <div className="font-mono font-bold text-slate-700">{service.quantityUsed || 0} {einheit}</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] uppercase text-slate-400 font-bold">Abgerechnet</div>
                        <div className="font-mono font-bold text-emerald-600">{service.quantityBilled || 0} {einheit}</div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                        <div className="text-[11px] uppercase text-indigo-400 font-bold">Offen</div>
                        <div className="font-mono font-bold text-indigo-600">{offen} {einheit}</div>
                    </div>
                </div>

                {bereitsVerzichtet > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4">
                        <div className="text-xs text-slate-600">
                            Bereits nicht mehr abgerechnet: <strong className="font-mono">{bereitsVerzichtet} {einheit}</strong>
                            {service.waivedReason && <span className="block mt-1 text-slate-500">Grund: {service.waivedReason}</span>}
                            {service.waivedBy && (
                                <span className="block text-slate-400 text-[11px] mt-0.5">
                                    {service.waivedBy}
                                    {service.waivedAt ? `, ${new Date(service.waivedAt).toLocaleDateString('de-DE')}` : ''}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            disabled={laeuft}
                            onClick={() => ausfuehren(() => onRelease(grund.trim()))}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2 disabled:opacity-50"
                        >
                            <RotateCcw className="w-3 h-3" /> Verzicht aufheben (steht wieder zur Abrechnung)
                        </button>
                    </div>
                )}

                {offen > 0 && (
                    <>
                        {waehlbar.length > 0 && (
                            <div className="flex gap-2 mb-3">
                                <button
                                    type="button"
                                    disabled={laeuft}
                                    onClick={() => setModus('entries')}
                                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${istAuswahl ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                >
                                    Bestimmte Leistungen wählen
                                </button>
                                <button
                                    type="button"
                                    disabled={laeuft}
                                    onClick={() => setModus('quantity')}
                                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${!istAuswahl ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                >
                                    Nur Menge
                                </button>
                            </div>
                        )}

                        {istAuswahl ? (
                            <>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-2">
                                    Worauf wird verzichtet?
                                </label>
                                <div className="space-y-1.5 mb-3 max-h-64 overflow-y-auto pr-1">
                                    {eintraege.map((e) => {
                                        const id = String(e.entry.id);
                                        const gewaehlt = auswahl.includes(id);
                                        return (
                                            <label
                                                key={id}
                                                title={e.blocked ? WAIVER_BLOCK_TEXT[e.blocked] : undefined}
                                                className={`flex items-start gap-2 p-2.5 rounded-lg border ${
                                                    e.blocked
                                                        ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                                                        : gewaehlt
                                                          ? 'border-indigo-300 bg-indigo-50 cursor-pointer'
                                                          : 'border-slate-200 hover:bg-slate-50 cursor-pointer'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={gewaehlt}
                                                    disabled={laeuft || Boolean(e.blocked)}
                                                    onChange={() => umschalten(id)}
                                                    className="mt-0.5"
                                                />
                                                <span className="text-sm min-w-0">
                                                    <strong>{tag(e.entry.date)}</strong> — {e.quantity} {einheit}
                                                    {e.blocked && (
                                                        <span className="ml-2 text-[10px] uppercase font-bold text-slate-500 bg-slate-200 rounded px-1.5 py-0.5">
                                                            {WAIVER_BLOCK_TEXT[e.blocked]}
                                                        </span>
                                                    )}
                                                    <span className="block text-[11px] text-slate-500 mt-0.5 truncate">
                                                        {e.entry.performerName || 'ohne Namen'}
                                                        {e.entry.description ? ` · ${e.entry.description}` : ''}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <p className="text-sm text-slate-600 mb-4">
                                    Gewählt: <strong className="font-mono">{auswahlSumme} {einheit}</strong> von {offen} {einheit} offen.
                                    {auswahlSumme > offen && (
                                        <span className="block text-rose-600 text-xs mt-1">
                                            Mehr als offen ist — bitte weniger wählen.
                                        </span>
                                    )}
                                </p>
                            </>
                        ) : (
                            <>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                                    Menge, auf die verzichtet wird
                                </label>
                                <div className="flex items-center gap-2 mb-1">
                                    <input
                                        type="number"
                                        min={0}
                                        max={offen}
                                        step="any"
                                        value={menge}
                                        disabled={laeuft}
                                        onChange={(e) => setMenge(e.target.value)}
                                        className="w-32 border border-slate-300 rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <span className="text-sm text-slate-500">{einheit} von {offen} {einheit} offen</span>
                                </div>
                                <p className="text-[11px] text-slate-400 mb-4">
                                    {waehlbar.length > 0
                                        ? 'Ohne Bezug zu einem Termin. Später steht nur die Menge da, nicht welche Leistung gemeint war.'
                                        : 'Zu dieser Position steht kein offener Protokolleintrag zur Wahl — es bleibt die Menge.'}
                                </p>
                            </>
                        )}

                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                            Begründung (Pflicht)
                        </label>
                        <textarea
                            value={grund}
                            disabled={laeuft}
                            rows={3}
                            onChange={(e) => setGrund(e.target.value)}
                            placeholder="z. B. Schlussrechnung ist gestellt, die Begehung wird nicht nachgefordert."
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <p className="text-[11px] text-slate-400 mt-1 mb-4">
                            Sie steht später im Leistungsprotokoll und in den Projektnotizen. Ohne
                            Begründung ist der Verzicht im Streitfall wertlos.
                        </p>
                    </>
                )}

                {offen <= 0 && bereitsVerzichtet <= 0 && (
                    <p className="text-sm text-slate-500 mb-4">
                        An dieser Position ist nichts offen, worauf verzichtet werden könnte.
                    </p>
                )}

                {fehler && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700">{fehler}</div>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                    {offen > 0 && (
                        <Button
                            variant="primary"
                            disabled={laeuft || !mengeOk || !grundOk}
                            onClick={() => ausfuehren(() => onWaive(mengeZahl, grund.trim(), istAuswahl ? [...auswahl] : undefined))}
                            icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                        >
                            Nicht mehr abrechnen
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
